import { syncService } from '../services/sync.service'
import { OfflinePatient, OfflineConsultation } from '../types/database.types'

/**
 * Add a newly created offline patient to the sync queue
 */
export async function queuePatientForSync(patient: OfflinePatient): Promise<void> {
    await syncService.addToSyncQueue({
        entityType: 'patient',
        entityId: patient._id,
        operation: 'create',
        priority: 2, // High priority for patients
        dependencies: [],
        maxRetries: 3,
        payload: patient,
        data: patient,
        timestamp: Date.now()
    })
}

/**
 * Add a newly created offline consultation to the sync queue
 */
export async function queueConsultationForSync(consultation: OfflineConsultation, patientId?: string): Promise<void> {
    const dependencies: string[] = []
    // If patient is not yet synced, try to depend on the patient's queue item id
    if (patientId && consultation._patientSyncRequired) {
        try {
            const queueItems = await syncService.getQueueItems()
            const patientQueueItem = queueItems.find((item) => item.entityType === 'patient' && item.entityId === patientId)
            if (patientQueueItem) {
                dependencies.push(patientQueueItem.id)
            }
        } catch {
            // Ignore lookup failures; scanning will correct dependencies later
        }
    }

    await syncService.addToSyncQueue({
        entityType: 'consultation',
        entityId: consultation._id,
        operation: 'create',
        priority: 1, // Lower priority than patients
        dependencies,
        maxRetries: 3,
        payload: consultation,
        data: consultation,
        timestamp: Date.now()
    })
}

/**
 * Add a patient update to the sync queue
 */
export async function queuePatientUpdateForSync(patient: OfflinePatient): Promise<void> {
    await syncService.addToSyncQueue({
        entityType: 'patient',
        entityId: patient._id,
        operation: 'update',
        priority: 1,
        dependencies: [],
        maxRetries: 3,
        payload: patient,
        data: patient,
        timestamp: Date.now()
    })
}

/**
 * Add a consultation update to the sync queue
 */
export async function queueConsultationUpdateForSync(consultation: OfflineConsultation): Promise<void> {
    await syncService.addToSyncQueue({
        entityType: 'consultation',
        entityId: consultation._id,
        operation: 'update',
        priority: 1,
        dependencies: [],
        maxRetries: 3,
        payload: consultation,
        data: consultation,
        timestamp: Date.now()
    })
}

/**
 * Queue a document for sync
 */
export async function queueDocumentForSync(documentId: string, parentEntityId: string): Promise<void> {
    // Let the scanning step compute correct dependencies and ordering;
    // we enqueue with empty dependencies to avoid wrong identifiers.
    await syncService.addToSyncQueue({
        entityType: 'document',
        entityId: documentId,
        operation: 'create',
        priority: 0, // Lowest priority
        dependencies: [],
        maxRetries: 3,
        payload: { documentId, parentEntityId },
        data: { documentId, parentEntityId },
        timestamp: Date.now()
    })
} 